// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import { IconDelete } from 'components/atoms/icons/IconDelete';
import { IconEditIC } from 'components/atoms/icons/IconEditIC';
import { IconPlusCircle } from 'components/atoms/icons/IconPlusCircle';
import { TareInput } from 'components/molecules/CreateUpdateTareInputForm';
import CreateUpdateTareModal, {
  AddEditTareProps,
} from 'components/organisms/CreateUpdateTareModal';
import { PrimaryTemplate } from 'components/templates/PrimaryTemplate';
import DOMPurify from 'dompurify';
import { useEffect, useState } from 'react';
import DeleteItemDarkModal from 'components/molecules/DeleteItemDarkModal';
import { createAxios } from 'ts/createAxios';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectUser } from 'redux/slices/userSlice';
import { DbManager } from 'util/DbManager';
import { ModalType } from 'util/Enums';
import { TareItem as OfflineTareItem } from 'util/Types';
import { normalizeNumber } from 'util/commons';
import { ExpandableList } from 'components/organisms/ExpandableList';
import { OptionItem } from 'components/molecules/SelectFieldMultipleChoise';
import { handleError } from 'util/errorHandler';
import styles from './TareWeightSubtraction.module.scss';

export interface TareItem {
  id: number;
  name: string;
  weight: number;
  tenants: OptionItem[];
}

interface FetchTaresRes {
  tares: TareItem[] | OfflineTareItem[];
  total: number;
}

const TareWeightSubtraction = () => {
  const api = createAxios();
  const user = useSelector(selectUser);
  const { t } = useTranslation();
  const [modalAddEditTare, setModalData] = useState<
    AddEditTareProps | undefined
  >();
  const [data, setData] = useState<FetchTaresRes>({
    total: 0,
    tares: [],
  });
  const [deleteItem, setDeleteItem] = useState<TareItem | undefined>();

  const fetchTares = () => {
    api
      .get<FetchTaresRes>(`/tares?siteId=${user.siteId}`)
      .then(async (res) => {
        setData(res.data);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const items = res.data.tares.map((tare: any) => ({
          ...tare,
          siteId: user.siteId,
        }));

        await DbManager.tares.clear();
        await DbManager.tares.bulkAdd(items);
      })
      .catch((e) => {
        handleError(e);
      });
  };

  useEffect(() => {
    fetchTares();
  }, []);

  const handleEdit = (data: TareInput, id: number) => {
    api
      .put(`/tares/${id}`, {
        name: data.name.trim(),
        weight: data.weight,
        tenantIds: data.tenantIds || [],
      })
      .then(() => {
        fetchTares();
        toast.success(t('messages.M_011'));
        setModalData(undefined);
      })
      .catch((e) => {
        handleError(e);
      });
  };

  const handleCreate = (data: TareInput) => {
    api
      .post('/tares', {
        name: data.name.trim(),
        weight: String(Number(data.weight)),
        tenantIds: data.tenantIds || [],
      })
      .then(() => {
        fetchTares();
        toast.success(t('messages.M_010'));
        setModalData(undefined);
      })
      .catch((e) => {
        handleError(e);
      });
  };

  const handleDelete = (id?: string | number) => {
    api
      .delete(`/tares/${id}`)
      .then(() => {
        fetchTares();
        toast.success(t('messages.M_012'));
        setDeleteItem(undefined);
      })
      .catch((e) => {
        handleError(e);
      });
  };
  return (
    <PrimaryTemplate
      h1={t('tare_management.header')}
      className={styles.tareWeightSubtraction}
    >
      <div className={styles.tareWeightSubtractionContent}>
        <div className={styles.title}>
          <div
            className={styles.countItem}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                t('common.page', {
                  page: data.total,
                }) ?? ''
              ),
            }}
          ></div>

          <button
            className={styles.submitButton}
            onClick={() => {
              setModalData({
                type: ModalType.add,
              });
            }}
          >
            <div className={styles.iconAdd}>
              <IconPlusCircle />
            </div>
            <span>{t('tare_management.add')}</span>
          </button>
        </div>
        <div className={styles.table}>
          <table id={styles.tareWeightSubtractionTable}>
            <thead>
              <tr>
                <th className={styles.tareName}>
                  {t('tare_management.label_name')}
                </th>
                <th className={styles.tenants}>
                  {t('tare_management.label_tenant')}
                </th>
                <th className={styles.weight}>
                  {t('tare_management.label_weight')}
                </th>
                <th colSpan={2} className={styles.actionField}>
                  {t('tare_management.label_action')}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.tares && data.tares.length > 0 ? (
                data.tares.map((item, i) => {
                  const tenantList: string[] = item.tenants
                    .map((tenant) => tenant.name)
                    .sort();

                  return (
                    <tr key={i}>
                      <td className={styles.tareName}>
                        <p title={item.name}>{item.name}</p>
                      </td>
                      <td className={styles.tenants}>
                        <ExpandableList
                          data={tenantList}
                          emptyRender={
                            <p>{t('tare_management.all_tenants_linked')}</p>
                          }
                        />
                      </td>
                      <td className={styles.weight}>
                        <p>
                          {normalizeNumber({ value: item.weight })}
                          <span>kg</span>
                        </p>
                      </td>
                      <td className={styles.actionField1}>
                        <div className={styles.contentAction}>
                          <div
                            className={styles.actionEdit}
                            onClick={() => {
                              setModalData({
                                type: ModalType.update,
                                tare: item,
                              });
                            }}
                          >
                            <IconEditIC />
                          </div>
                        </div>
                      </td>
                      <td className={styles.actionField1}>
                        <div className={styles.contentAction}>
                          <div
                            className={styles.actionDelete}
                            onClick={() => {
                              setDeleteItem(item);
                            }}
                          >
                            <IconDelete />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={3} className={styles.msgError}>
                    {t('messages.M_008')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {modalAddEditTare && (
        <CreateUpdateTareModal
          onClose={() => {
            setModalData(undefined);
          }}
          tareData={modalAddEditTare}
          onSubmit={(data, id) => {
            if (id) {
              handleEdit(data, id);
            } else {
              handleCreate(data);
            }
          }}
        />
      )}
      {deleteItem && (
        <DeleteItemDarkModal
          deletingId={deleteItem.id}
          deletingName={deleteItem.name}
          onClose={() => {
            setDeleteItem(undefined);
          }}
          onHandleDelete={handleDelete}
        />
      )}
    </PrimaryTemplate>
  );
};

export default TareWeightSubtraction;
